import {
  FaCcAmex,
  FaCcDinersClub,
  FaCcDiscover,
  FaCcJcb,
  FaCcMastercard,
  FaCcVisa,
  FaCreditCard,
} from "react-icons/fa";

import TermsModal from "@/components/modal/TermsModal";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getInstallments } from "@/services/flowQuote";
import { Installment } from "@/types/FlowQuote";
import { formatMoneyBr } from "@/utils/format";
import * as valid from "card-validator";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { IconType } from "react-icons";
import CouponComponent from "./coupon-component";
import { PersonalInfoDisplay } from "./personal-info-display";
import type { PaymentFormValues } from "./payment-types";

interface CreditCardFormProps {
  quotePrice: number;
  disabled?: boolean;
}

export default function CreditCardForm({
  quotePrice,
  disabled = false,
}: CreditCardFormProps) {
  const form = useFormContext<PaymentFormValues>();

  const [installments, setInstallments] = useState<Installment[]>([]);
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const [isTermsModalOpen, setTermsModalOpen] = useState(false);

  useEffect(() => {
    const fetchInstallments = async () => {
      try {
        setLoadingInstallments(true);
        const data = await getInstallments(quotePrice);
        if (Array.isArray(data) && data.length > 0) {
          setInstallments(data);
        } else {
          setInstallments([
            {
              parcelas: "1",
              valor: String(quotePrice),
              juros_perc: "0",
              juros_valor: "0",
              total: String(quotePrice),
            },
          ]);
        }
      } catch {
        setInstallments([
          {
            parcelas: "1",
            valor: String(quotePrice),
            juros_perc: "0",
            juros_valor: "0",
            total: String(quotePrice),
          },
        ]);
      } finally {
        setLoadingInstallments(false);
      }
    };

    fetchInstallments();
  }, [quotePrice]);

  const handleCardNumberChange = (value: string) => {
    const numberValidation = valid.number(value);
    if (numberValidation.card) {
      form.setValue("card1.brand", numberValidation.card.type);
    }
  };

  const CARD_BRANDS: Record<string, { label: string; icon: IconType }> = {
    visa: { label: "Visa", icon: FaCcVisa },
    mastercard: { label: "Mastercard", icon: FaCcMastercard },
    amex: { label: "American Express", icon: FaCcAmex },
    diners: { label: "Diners Club", icon: FaCcDinersClub },
    discover: { label: "Discover", icon: FaCcDiscover },
    jcb: { label: "JCB", icon: FaCcJcb },
    other: { label: "Outro", icon: FaCreditCard },
  };

  function formatCardNumber(value: string) {
    const onlyNumbers = value.replace(/\D/g, "");
    return onlyNumbers.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  }

  return (
    <div className="space-y-6">
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      />
      <div className="flex flex-col gap-4">
        <h3 className="text-2xl font-semibold text-n-green">
          Cartão de crédito
        </h3>

        {disabled && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 text-yellow-800">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium text-sm">
                Pagamento com cartão de crédito temporariamente indisponível
              </span>
            </div>
            <p className="text-yellow-700 text-sm mt-1">
              Utilize PIX ou boleto bancário para finalizar sua compra.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <p className="font-bold  text-n-text-neutral mb-2">
            Valor total: <span className="">{formatMoneyBr(quotePrice)}</span>
          </p>

          <CouponComponent />
        </div>

        <div className="">
          <h4 className="font-medium mb-3">Informações do cartão</h4>
          <div className="space-y-3">
            <FormField
              control={form.control}
              name="card1.name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      id="card1Name"
                      {...field}
                      placeholder="Nome como está no cartão"
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="card1.number"
              render={({ field }) => {
                const currentBrand = form.watch("card1.brand");
                const brandInfo = currentBrand
                  ? CARD_BRANDS[currentBrand]
                  : CARD_BRANDS["outher"];
                const BrandIcon = brandInfo?.icon;

                return (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input
                          id="card1Number"
                          placeholder="Número do cartão"
                          inputMode="numeric"
                          {...field}
                          value={formatCardNumber(field.value)}
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/\D/g, "");
                            handleCardNumberChange(rawValue);
                            field.onChange(rawValue);
                          }}
                          maxLength={19}
                          className="pr-10"
                          disabled={disabled}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-gray-600">
                          {BrandIcon ? (
                            <BrandIcon className="h-5 w-5" />
                          ) : currentBrand ? (
                            <span className="text-xs capitalize">
                              {currentBrand}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <div className="grid grid-cols-10 gap-2">
              <div className="col-span-4">
                <FormField
                  control={form.control}
                  name="card1.expiry"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          id="card1Expiry"
                          placeholder="MM/AA"
                          inputMode="numeric"
                          {...field}
                          value={field.value}
                          onChange={(e) => {
                            let value = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 4);
                            if (value.length >= 3) {
                              value = value.replace(
                                /^(\d{2})(\d{1,2})/,
                                "$1/$2",
                              );
                            }
                            field.onChange(value);
                          }}
                          maxLength={5}
                          disabled={disabled}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="col-span-3">
                <FormField
                  control={form.control}
                  name="card1.cvv"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          id="card1CVV"
                          placeholder="CVV"
                          inputMode="numeric"
                          {...field}
                          value={field.value}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 4);
                            field.onChange(value);
                          }}
                          maxLength={4}
                          disabled={disabled}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="installments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="card1Installments">
                    Quantidade de parcelas
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={loadingInstallments || disabled}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            loadingInstallments ? "Carregando..." : "Selecione"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {installments.map((item) => {
                        const hasInterest = Number(item.juros_valor) > 0;
                        return (
                          <SelectItem key={item.parcelas} value={item.parcelas}>
                            {item.parcelas}x de{" "}
                            {formatMoneyBr(Number(item.valor))}{" "}
                            {hasInterest && "(com juros)"}{" "}
                            {hasInterest && (
                              <span className="text-xs text-n-text-neutral">
                                (Total {formatMoneyBr(Number(item.total))})
                              </span>
                            )}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="border-t pt-4 mt-6">
          <h4 className="font-medium mb-4">Dados do titular:</h4>
          <PersonalInfoDisplay />
        </div>

        <FormField
          control={form.control}
          name="termsAccepted"
          render={({ field }) => (
            <FormItem className="flex items-start justify-start gap-2 ">
              <FormControl>
                <Checkbox
                  className="mt-2"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  id="terms-bank"
                  disabled={disabled}
                />
              </FormControl>
              <div className="flex-1 flex flex-col">
                <Label
                  htmlFor="terms-bank"
                  className="text-sm text-n-text mb-2"
                >
                  Declaro que li e concordo com os{" "}
                  <span
                    className="text-n-primary font-medium cursor-pointer underline hover:text-n-primary/80"
                    onClick={() => setTermsModalOpen(true)}
                  >
                    Termos e Condições
                  </span>{" "}
                  de serviço
                </Label>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
